import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../components/hooks/useToast';
import { formatDate, formatAmount } from '../../../utils/global'; // Simplified: Only formatAmount for all numbers
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';

const WireTransferViewerPage = () => {
  const { processId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wireTransferData, setWireTransferData] = useState([]);
  const [fileInfo, setFileInfo] = useState(null);
  const [wireTransferConfig, setWireTransferConfig] = useState(null);

  // Fetch wire transfer configuration
  const fetchWireTransferConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');
      
      if (!tenantId) {
        throw new Error('Tenant information not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=WIRE_TRANSFER_${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch wire transfer config: ${response.status}`);
      }
      
      const config = await response.json();
      setWireTransferConfig(config);
      return config;
    } catch (err) {
      console.error('Error fetching wire transfer config:', err);
      showError('Failed to load wire transfer configuration');
      throw err;
    }
  };

  // Fetch wire transfer data
  const fetchWireTransferData = async (showLoading = true) => {
    if (!processId) return;
    
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');
      
      if (!tenantId) {
        throw new Error('Tenant information not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/wire-transfer/jpmc-freeform/${tenantId}?process_id=${processId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      const data = Array.isArray(responseData) ? responseData : [responseData];
      
      if (data.length === 0) {
        throw new Error('No wire transfer data found');
      }
      
      // Ensure all fields from config are present in data rows
      if (wireTransferConfig?.grid?.columns) {
        const dataWithFields = data.map(item => {
          const newItem = { ...item };
          wireTransferConfig.grid.columns.forEach(col => {
            if (newItem[col.name] === undefined) {
              newItem[col.name] = null;
            }
          });
          // Ensure ID if missing
          newItem.id = item.id || item._id || `row-${Math.random().toString(36).substr(2, 9)}`;
          return newItem;
        });
        setWireTransferData(dataWithFields);
      } else {
        // Fallback: Add IDs
        const dataWithIds = data.map((item, index) => ({
          ...item,
          id: item.id || item._id || `row-${index}`,
        }));
        setWireTransferData(dataWithIds);
      }
      
      showSuccess('Wire transfer data loaded successfully');
    } catch (err) {
      console.error('Error fetching wire transfer data:', err);
      setError(err.message || 'Failed to load wire transfer data');
      setWireTransferData([]);
      showError(err.message || 'Failed to load wire transfer data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };
  
  // Handle manual refresh
  const handleRefresh = () => {
    fetchWireTransferData(true);
  };

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchWireTransferConfig();
        await fetchWireTransferData();
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    
    loadData();
  }, [processId]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Generate columns from configuration using amount format for all numbers
  const generateColumnsFromConfig = (config) => {
    if (!config || !config.grid || !config.grid.columns) return [];
   
    return config.grid.columns.map(column => {
      const dataType = column.DataType?.toLowerCase() || 'string';
      const isDate = dataType === 'date' || dataType === 'datetime';
      const isNumber = dataType === 'number' || dataType === 'integer' || dataType === 'float';
     
      return {
        field: column.name,
        headerName: column.title || column.name,
        type: isNumber ? 'number' : 'string',
        width: column.width || 150,
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return value;
        },
        valueFormatter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return '';
         
          try {
            if (isDate) {
              return formatDate(value);
            } else if (isNumber) {
              const numValue = parseFloat(value);
              if (isNaN(numValue)) return value;
             
              // Always use formatAmount for numbers now
              return formatAmount(numValue);
            }
          } catch (e) {
            console.error(`Error formatting value for ${column.name}:`, e);
            return value; // Return original value on error
          }
         
          return String(value);
        },
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') return '-';
         
          // Special handling for status-like columns
          const statusColumns = ['status', 'state', 'chargeback_status', 'win_loss'];
          if (statusColumns.some(statusCol => column.name.toLowerCase().includes(statusCol))) {
            const statusColors = {
              completed: 'success',
              pending: 'warning',
              failed: 'error',
              won: 'success',
              lost: 'error',
              approved: 'success',
              declined: 'error',
              open: 'info',
              closed: 'default',
              in_progress: 'warning',
              resolved: 'success'
            };
           
            const color = statusColors[String(value).toLowerCase()] || 'default';
           
            return (
              <Chip
                label={value}
                size="small"
                color={color}
                variant={color === 'warning' || color === 'info' ? 'outlined' : 'filled'}
              />
            );
          }
         
          // For date and number fields, use the formatted value from valueFormatter
          if (isDate || isNumber) {
            return params.formattedValue || value;
          }
         
          return String(value);
        },
        sortable: true,
        filterable: true,
      };
    });
  };

  // Fallback column generation based on data (if no config) - with amount format for numbers
  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    // Get all unique keys from the data
    const allKeys = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    return Array.from(allKeys).map(key => {
      const isDate = key.toLowerCase().includes('date') || key.toLowerCase().includes('time');
      const isNumber = key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('price');
     
      return {
        field: key,
        headerName: key.includes('_') ? key : key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim(),
        flex: 1,
        minWidth: 150,
        headerClassName: 'MuiDataGrid-columnHeader',
        cellClassName: 'MuiDataGrid-cell',
        type: isNumber ? 'number' : isDate ? 'date' : 'string',
        valueFormatter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return '';
         
          try {
            if (isDate) {
              return formatDate(value);
            } else if (isNumber) {
              const numValue = parseFloat(value);
              if (isNaN(numValue)) return value;
              return formatAmount(numValue); // Use amount format for all numeric fields
            }
          } catch (e) {
            return value;
          }
         
          return String(value);
        },
        renderCell: (params) => {
          if (isNumber || isDate) {
            return params.formattedValue || params.value || '-';
          }
          return params.value || '-';
        },
        sortable: true,
        filterable: true,
      };
    });
  };
  
  const [columns, setColumns] = useState([]);
  const apiRef = useGridApiRef();
  
  // Update columns when config or data changes
  useEffect(() => {
    if (wireTransferConfig?.grid?.columns && wireTransferData.length > 0) {
      const newColumns = generateColumnsFromConfig(wireTransferConfig);
      setColumns(newColumns);
    } else if (wireTransferData.length > 0) {
      // Fallback to data-based generation with amount formatting
      setColumns(generateColumns(wireTransferData));
    }
  }, [wireTransferConfig, wireTransferData]);

  return (
    <Box sx={{ ...harmonatePageSx, p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 3,
          '& .MuiBreadcrumbs-separator': { color: ht.white },
          '& a, & .MuiTypography-root': { color: 'rgba(255,255,255,0.9)' },
        }}
      >
        <Link
          color="inherit"
          href="/treasury"
          onClick={(e) => {
            e.preventDefault();
            navigate('/treasury');
          }}
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: ht.white } }}
        >
          Treasury
        </Link>
        <Typography sx={{ color: ht.white }}>Wire Transfer</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleBack} sx={{ ...harmonatePlainIconButtonSx, mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ ...harmonatePageTitleSx, mb: 0 }}>
            Wire Transfer Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => apiRef.current.exportDataAsCsv()}
            startIcon={<GetAppIcon />}
            disabled={loading || wireTransferData.length === 0}
            sx={harmonateUploadOutlinedSx}
          >
            Export
          </Button>
          <Tooltip title={loading ? 'Loading...' : 'Refresh'}>
            <IconButton 
              onClick={handleRefresh}
              disabled={loading}
              sx={harmonatePlainIconButtonSx}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File info section */}
      {fileInfo && (
        <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ color: ht.text, fontWeight: 600 }}>
            File: {fileInfo.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Uploaded: {new Date(fileInfo.uploadedAt).toLocaleString()}
          </Typography>
        </Paper>
      )}

      {/* DataGridPro with PDFViewer style */}
      <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white, height: 'calc(100vh - 250px)', width: '100%', p: 1, flex: 1, minHeight: 0 }}>
        <DataGridPro
          apiRef={apiRef}
          rows={wireTransferData}
          columns={columns}
          pagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={20}
          autoPageSize={false}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-cell': { 
              ...harmonateDataGridSx['& .MuiDataGrid-cell'],
              cursor: 'default',
              px: 1.5,
            },
            '& .MuiDataGrid-columnHeader': { 
              px: 1.5,
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              ...harmonateDataGridSx['& .MuiDataGrid-row'],
              cursor: 'default',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${ht.line}`,
              minHeight: '52px',
              position: 'relative',
              zIndex: 1,
            },
            '& .MuiDataGrid-paginationPanel': { 
              fontSize: '0.75rem' 
            },
            '& .MuiTablePagination-root': { 
              fontSize: '0.75rem' 
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { 
              fontSize: '0.75rem' 
            },
          }}
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body2" color="textSecondary">
                  {loading ? 'Loading...' : 'No wire transfer data available'}
                </Typography>
              </Box>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

export default WireTransferViewerPage;